// AudioEngine: owns the AudioContext, builds the mixer graph from project state,
// runs the transport (play/stop/record position), schedules clip playback,
// the metronome, and exposes metering. UI and recorder talk to this.
import { Store } from "../state/store";
import { Track, windowedNotes } from "../state/model";
import { createEffectNode, EffectNode } from "./effects";
import { Instrument } from "./instrument";
import { createInstrument } from "./gm";

interface Channel {
  fxInput: GainNode; // clips/inputs connect here
  panner: StereoPannerNode;
  fader: GainNode;
  analyser: AnalyserNode;
  fxNodes: FxSlot[];
}

/** A live effect node plus the params it last applied (so unchanged effects
 *  aren't re-pushed on every store change). */
interface FxSlot { id: string; node: EffectNode; applied: string; }

export class AudioEngine {
  ctx: AudioContext;
  private store: Store;
  private channels = new Map<string, Channel>();
  private masterFxInput!: GainNode;
  private masterFader!: GainNode;
  private masterAnalyser!: AnalyserNode;
  private masterFxNodes: FxSlot[] = [];
  private masterSig = ""; // structure the master chain was last built for

  /** Externally-managed insert nodes (e.g. WAM plugins), keyed by trackId or "master". */
  externalInserts = new Map<string, AudioNode[]>();

  /** Instruments (synth/GM/drums/sampler/soundfont/WAM) for instrument tracks. */
  private instruments = new Map<string, Instrument>();
  private instrumentSig = new Map<string, string>(); // trackId → JSON of its instrument def

  private sources = new Set<AudioBufferSourceNode>();
  playing = false;
  recording = false;
  private startCtxTime = 0;
  private startPos = 0;
  private pausedPos = 0;
  private loopTimer: any = null;

  metronome = false;
  private metroTimer: any = null;
  private metroNextBeatTime = 0;
  private metroBeat = 0;

  onTransport?: () => void; // fired on play/stop/record state changes

  private meterBuf = new Float32Array(1024);

  constructor(store: Store) {
    this.store = store;
    this.ctx = new AudioContext({ latencyHint: "interactive" });
    this.buildMaster();
    store.on("change", ({ scope }) => {
      if (scope === "tracks" || scope === "mixer" || scope === "project") this.syncGraph();
    });
    this.syncGraph();
  }

  async resume() {
    if (this.ctx.state !== "running") await this.ctx.resume();
  }

  /** Route output to a specific device (Chrome 110+). No-op where unsupported. */
  async setSinkId(deviceId: string) {
    const anyCtx = this.ctx as any;
    if (typeof anyCtx.setSinkId === "function") {
      try { await anyCtx.setSinkId(deviceId); } catch (e) { console.warn("setSinkId", e); }
    }
  }

  // ---- graph construction ---------------------------------------------
  private buildMaster() {
    this.masterFxInput = this.ctx.createGain();
    this.masterFader = this.ctx.createGain();
    this.masterAnalyser = this.ctx.createAnalyser();
    this.masterAnalyser.fftSize = 2048;
    this.masterFader.connect(this.masterAnalyser);
    this.masterAnalyser.connect(this.ctx.destination);
    this.rebuildMasterFx();
  }

  /** Which master inserts should be live — rebuild only when this changes. */
  private masterWantSig(): string {
    return this.store.project.masterFx.filter((f) => f.enabled && f.kind !== "wam").map((f) => f.id).join(",")
      + "|" + (this.externalInserts.get("master")?.length ?? 0);
  }

  private rebuildMasterFx() {
    this.masterFxNodes.forEach((f) => f.node.dispose());
    this.masterFxNodes = [];
    // Drop every old route out of the master input first — otherwise the direct
    // input→fader path from an earlier (empty) chain survives and the dry signal
    // is summed around the inserts.
    try { this.masterFxInput.disconnect(); } catch {}
    let cursor: AudioNode = this.masterFxInput;
    for (const fx of this.store.project.masterFx) {
      if (fx.kind === "wam" || !fx.enabled) continue; // WAMs are wired by their host; disabled = bypassed
      const node = createEffectNode(this.ctx, fx);
      cursor.connect(node.input);
      cursor = node.output;
      this.masterFxNodes.push({ id: fx.id, node, applied: JSON.stringify(fx.params) });
    }
    for (const node of this.externalInserts.get("master") ?? []) { cursor.connect(node); cursor = node; }
    cursor.connect(this.masterFader);
    this.masterSig = this.masterWantSig();
    this.glide(this.masterFader.gain, this.store.project.masterVolume);
  }

  /** Push changed params into live effect nodes (skips unchanged ones). */
  private updateFx(slots: FxSlot[], fxList: { id: string; params: any }[]) {
    for (const fx of fxList) {
      const slot = slots.find((f) => f.id === fx.id);
      if (!slot) continue;
      const sig = JSON.stringify(fx.params);
      if (slot.applied !== sig) { slot.node.update(fx.params); slot.applied = sig; }
    }
  }

  private primed = new WeakSet<AudioParam>();
  /** Set a mixer param: exact the first time, then a short glide (no zipper clicks). */
  private glide(p: AudioParam, v: number) {
    if (!this.primed.has(p)) { this.primed.add(p); p.value = v; return; }
    if (Math.abs(p.value - v) < 1e-6) return;
    p.cancelScheduledValues(this.ctx.currentTime);
    p.setTargetAtTime(v, this.ctx.currentTime, 0.015);
  }

  private buildChannel(track: Track): Channel {
    const fxInput = this.ctx.createGain();
    const panner = this.ctx.createStereoPanner();
    const fader = this.ctx.createGain();
    const analyser = this.ctx.createAnalyser();
    analyser.fftSize = 1024;
    const ch: Channel = { fxInput, panner, fader, analyser, fxNodes: [] };
    this.wireChannelFx(track, ch);
    return ch;
  }

  private wireChannelFx(track: Track, ch: Channel) {
    ch.fxNodes.forEach((f) => f.node.dispose());
    ch.fxNodes = [];
    try { ch.fxInput.disconnect(); } catch {}
    let cursor: AudioNode = ch.fxInput;
    for (const fx of track.fx) {
      if (fx.kind === "wam" || !fx.enabled) continue;
      const node = createEffectNode(this.ctx, fx);
      cursor.connect(node.input);
      cursor = node.output;
      ch.fxNodes.push({ id: fx.id, node, applied: JSON.stringify(fx.params) });
    }
    for (const node of this.externalInserts.get(track.id) ?? []) { cursor.connect(node); cursor = node; }
    cursor.connect(ch.panner);
    ch.panner.connect(ch.fader);
    ch.fader.connect(ch.analyser);
    ch.analyser.connect(this.masterFxInput);
  }

  /** Reconcile channels with current tracks; update live params. */
  syncGraph() {
    const ids = new Set(this.store.project.tracks.map((t) => t.id));
    // remove channels for deleted tracks
    for (const [id, ch] of this.channels) {
      if (!ids.has(id)) {
        ch.fxNodes.forEach((f) => f.node.dispose());
        [ch.fxInput, ch.panner, ch.fader, ch.analyser].forEach((n) => { try { n.disconnect(); } catch {} });
        this.channels.delete(id);
        this.instruments.get(id)?.dispose();
        this.instruments.delete(id);
        this.instrumentSig.delete(id);
      }
    }
    const anySolo = this.store.anySolo();
    for (const track of this.store.project.tracks) {
      let ch = this.channels.get(track.id);
      if (!ch) { ch = this.buildChannel(track); this.channels.set(track.id, ch); }
      // instrument tracks: (re)build when the instrument definition changes —
      // covers the panel, undo/redo, project load, AND remote sync (a peer that
      // receives a new instrument def must rebuild, or it keeps playing default).
      if (track.kind === "instrument" && track.instrument) {
        const sig = JSON.stringify(track.instrument);
        if (this.instrumentSig.get(track.id) !== sig) {
          this.instruments.get(track.id)?.dispose();
          const inst = createInstrument(this.ctx, track.instrument, this.store.buffers);
          inst.output.connect(ch.fxInput);
          this.instruments.set(track.id, inst);
          this.instrumentSig.set(track.id, sig);
        }
      }
      // rebuild fx chain if structure changed
      const liveIds = ch.fxNodes.map((f) => f.id).join(",");
      const wantIds = track.fx.filter((f) => f.enabled && f.kind !== "wam").map((f) => f.id).join(",");
      if (liveIds !== wantIds) this.wireChannelFx(track, ch);
      else this.updateFx(ch.fxNodes, track.fx);
      // live params (glided, so fader/pan moves don't click)
      const audible = !track.mute && (!anySolo || track.solo);
      this.glide(ch.fader.gain, audible ? track.volume : 0);
      this.glide(ch.panner.pan, track.pan);
    }
    // Master chain: rebuild only when its structure changes; otherwise just update
    // params. (Rebuilding on every change re-created reverbs/worklets on each
    // fader move and cut their tails.)
    if (this.masterWantSig() !== this.masterSig) this.rebuildMasterFx();
    else {
      this.updateFx(this.masterFxNodes, this.store.project.masterFx);
      this.glide(this.masterFader.gain, this.store.project.masterVolume);
    }
  }

  channelInput(trackId: string): GainNode | undefined {
    return this.channels.get(trackId)?.fxInput;
  }

  /** Master-bus input node — for connecting preview instruments (e.g. the step
   *  sequencer) so they're heard through the master. */
  previewDestination(): AudioNode { return this.masterFxInput; }

  /** The instrument for an instrument track (for live MIDI play + edits). */
  instrument(trackId: string): Instrument | undefined {
    return this.instruments.get(trackId);
  }

  /** Recreate a track's instrument from its current definition (e.g. after the
   *  user changes the synth preset → GM program). Returns its load promise. */
  rebuildInstrument(trackId: string): Promise<void> | undefined {
    const track = this.store.track(trackId);
    const ch = this.channels.get(trackId);
    if (!track || !track.instrument || !ch) return;
    this.instruments.get(trackId)?.dispose();
    const inst = createInstrument(this.ctx, track.instrument, this.store.buffers);
    inst.output.connect(ch.fxInput);
    this.instruments.set(trackId, inst);
    this.instrumentSig.set(trackId, JSON.stringify(track.instrument));
    return inst.ready;
  }
  /** Silence all held instrument notes (transport stop / panic). */
  allNotesOff() { for (const s of this.instruments.values()) s.allNotesOff(); }

  addExternalInsert(target: string, node: AudioNode) {
    const list = this.externalInserts.get(target) ?? [];
    list.push(node);
    this.externalInserts.set(target, list);
    this.rewire(target);
  }
  removeExternalInsert(target: string, node: AudioNode) {
    const list = this.externalInserts.get(target);
    if (!list) return;
    const i = list.indexOf(node);
    if (i >= 0) list.splice(i, 1);
    try { node.disconnect(); } catch {}
    this.rewire(target);
  }
  private rewire(target: string) {
    if (target === "master") { this.rebuildMasterFx(); return; }
    const track = this.store.track(target);
    const ch = this.channels.get(target);
    if (track && ch) this.wireChannelFx(track, ch);
  }

  // ---- transport -------------------------------------------------------
  /** A loop pass that has been scheduled but not reached yet (sample-accurate looping). */
  private nextPass: { at: number; pos: number } | null = null;

  /** While playing: the audio-clock time `at` when the timeline is at `pos`. */
  anchor(): { pos: number; at: number } | null {
    if (!this.playing) return null;
    this.position(); // folds in a pending loop pass
    return { pos: this.startPos, at: this.startCtxTime };
  }

  position(): number {
    if (!this.playing) return this.pausedPos;
    const t = this.ctx.currentTime;
    if (this.nextPass && t >= this.nextPass.at) {
      this.startPos = this.nextPass.pos; this.startCtxTime = this.nextPass.at; this.nextPass = null;
    }
    return this.startPos + Math.max(0, t - this.startCtxTime);
  }

  /** Start playback from `from`. With `at` (an audio-clock time), the timeline is
   *  at `from` exactly at `at` — used to start in step with a remote partner; if
   *  `at` has already passed, playback joins further along so it stays aligned. */
  async play(from?: number, at?: number) {
    await this.resume();
    if (this.playing) this.stopSources();
    clearTimeout(this.loopTimer); this.nextPass = null;
    let pos = from ?? this.pausedPos;
    let when = this.ctx.currentTime + 0.06;
    if (at != null && isFinite(at)) {
      const earliest = this.ctx.currentTime + 0.02;
      if (at < earliest) { pos += earliest - at; when = earliest; } else when = at;
    }
    this.startPos = pos;
    this.startCtxTime = when;
    this.playing = true;
    this.allNotesOff(); // clear any notes scheduled by a previous play/seek
    const loop = this.activeLoop(pos);
    this.scheduleClips(pos, this.startCtxTime, loop ? loop.end : Infinity);
    if (this.metronome) this.startMetronome(pos, this.startCtxTime);
    if (loop) this.armLoop(loop, pos, this.startCtxTime);
    this.onTransport?.();
  }

  /** The loop to apply when playing from `fromPos`: none while recording (the take
   *  is one continuous capture) or when starting past the loop's end. */
  private activeLoop(fromPos: number): { start: number; end: number } | null {
    if (!this.store.project.loopEnabled || this.recording) return null;
    const r = this.store.loopRange();
    if (!r || fromPos >= r.end - 1e-3) return null;
    return r;
  }

  /** Queue the next loop pass at the exact audio-clock time the current one ends.
   *  The current pass is already cut off at `loop.end`, so the seam is gapless. */
  private armLoop(loop: { start: number; end: number }, passPos: number, passCtxTime: number) {
    clearTimeout(this.loopTimer);
    const at = passCtxTime + (loop.end - passPos);
    const lead = 0.3; // schedule this far ahead of the seam
    this.loopTimer = setTimeout(() => {
      if (!this.playing) return;
      // Throttled timer (background tab) missed the seam → restart from the loop start.
      if (this.ctx.currentTime > at - 0.02) { void this.play(loop.start); return; }
      this.nextPass = { at, pos: loop.start };
      this.scheduleClips(loop.start, at, loop.end);
      if (this.metronome) { this.stopMetronome(); this.startMetronome(loop.start, at); }
      this.armLoop(loop, loop.start, at);
    }, Math.max(0, (at - this.ctx.currentTime - lead) * 1000));
  }

  /** Re-apply loop settings to a running transport (loop toggled / region edited). */
  refreshLoop() {
    if (this.playing && !this.recording) void this.play(this.position());
  }

  pause() {
    if (!this.playing) return;
    this.pausedPos = this.position();
    this.stopSources();
    this.allNotesOff();
    this.stopMetronome();
    clearTimeout(this.loopTimer); this.nextPass = null;
    this.playing = false;
    this.onTransport?.();
  }

  stop() {
    this.pausedPos = 0;
    this.stopSources();
    this.allNotesOff();
    this.stopMetronome();
    clearTimeout(this.loopTimer); this.nextPass = null;
    this.playing = false;
    this.recording = false;
    this.onTransport?.();
  }

  seek(pos: number) {
    const wasPlaying = this.playing;
    if (wasPlaying) this.stopSources();
    this.pausedPos = Math.max(0, pos);
    if (wasPlaying) this.play(this.pausedPos);
    else this.onTransport?.();
  }

  private stopSources() {
    for (const s of this.sources) { try { s.stop(); } catch {} try { s.disconnect(); } catch {} }
    this.sources.clear();
  }

  /** Schedule everything that plays from timeline `fromPos` (at audio time `when`)
   *  up to `untilPos` — the loop end when looping, so passes butt together. */
  private scheduleClips(fromPos: number, when: number, untilPos = Infinity) {
    for (const track of this.store.project.tracks) {
      const ch = this.channels.get(track.id);
      if (!ch) continue;
      const synth = this.instruments.get(track.id);
      for (const clip of track.clips) {
        if (clip.takeActive === false) continue;
        // MIDI clip → schedule its (trim-windowed) notes on the track's synth
        if (clip.notes) {
          if (!synth) continue;
          for (const n of windowedNotes(clip)) {
            const noteStart = clip.start + n.start;
            if (noteStart < fromPos) continue; // don't retrigger notes already underway
            if (noteStart >= untilPos) continue; // belongs to the next loop pass
            synth.scheduleNote(n.midi, n.vel, when + (noteStart - fromPos), n.dur);
          }
          continue;
        }
        const fullEnd = clip.start + clip.duration;
        const clipEnd = Math.min(fullEnd, untilPos);
        const cutByLoop = clipEnd < fullEnd - 1e-6;
        if (clipEnd <= fromPos) continue;
        const buffer = this.store.buffers.get(clip.assetId);
        if (!buffer) continue;
        const startInTimeline = Math.max(clip.start, fromPos);
        const bufferOffset = clip.offset + (startInTimeline - clip.start);
        const playDur = clipEnd - startInTimeline;
        if (playDur <= 0) continue;
        const src = this.ctx.createBufferSource();
        src.buffer = buffer;
        const cg = this.ctx.createGain();
        const startAt = when + (startInTimeline - fromPos);
        // fades (only when playing the clip from its head/tail)
        const baseGain = clip.gain;
        cg.gain.setValueAtTime(baseGain, startAt);
        if (clip.fadeIn > 0 && startInTimeline <= clip.start + 0.001) {
          cg.gain.setValueAtTime(0, startAt);
          cg.gain.linearRampToValueAtTime(baseGain, startAt + clip.fadeIn);
        }
        // fade-out at the clip's real end, or a 5 ms de-click where a loop cuts it
        const fo = cutByLoop ? Math.min(0.005, playDur) : clip.fadeOut;
        if (fo > 0) {
          const foStart = startAt + playDur - fo;
          cg.gain.setValueAtTime(baseGain, Math.max(startAt, foStart));
          cg.gain.linearRampToValueAtTime(0, startAt + playDur);
        }
        src.connect(cg);
        cg.connect(ch.fxInput);
        src.start(startAt, Math.max(0, bufferOffset), playDur);
        this.sources.add(src);
        src.onended = () => { this.sources.delete(src); try { cg.disconnect(); } catch {} };
      }
    }
  }

  // ---- metronome (lookahead scheduler) --------------------------------
  private startMetronome(fromPos: number, when: number) {
    const spb = 60 / this.store.project.tempo;
    this.metroBeat = Math.ceil(fromPos / spb);
    this.metroNextBeatTime = when + (this.metroBeat * spb - fromPos);
    const tick = () => {
      const ahead = this.ctx.currentTime + 0.2;
      while (this.metroNextBeatTime < ahead) {
        const accent = this.metroBeat % this.store.project.beatsPerBar === 0;
        this.clickAt(this.metroNextBeatTime, accent);
        this.metroBeat++;
        this.metroNextBeatTime += spb;
      }
    };
    tick();
    this.metroTimer = setInterval(tick, 60);
  }
  private stopMetronome() { clearInterval(this.metroTimer); this.metroTimer = null; }
  private clickAt(t: number, accent: boolean): OscillatorNode {
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.frequency.value = accent ? 1500 : 900;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(accent ? 0.5 : 0.3, t + 0.001);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
    osc.connect(g);
    g.connect(this.masterFader);
    osc.start(t);
    osc.stop(t + 0.06);
    return osc;
  }

  setMetronome(on: boolean) {
    this.metronome = on;
    if (on && this.playing) this.startMetronome(this.position(), this.ctx.currentTime + 0.06);
    if (!on) this.stopMetronome();
  }

  // ---- count-in --------------------------------------------------------
  private countInOscs = new Set<OscillatorNode>();
  private countInTimer: any = null;
  private countInResolve: (() => void) | null = null;

  /** Play `beats` metronome clicks (accenting each bar start) and resolve when
   *  done — a record count-in. Independent of the metronome toggle. */
  countIn(beats: number): Promise<void> {
    this.cancelCountIn();
    return new Promise((resolve) => {
      this.countInResolve = resolve;
      const spb = 60 / this.store.project.tempo;
      const start = this.ctx.currentTime + 0.12;
      const bpb = this.store.project.beatsPerBar;
      for (let i = 0; i < beats; i++) {
        const osc = this.clickAt(start + i * spb, i % bpb === 0);
        this.countInOscs.add(osc);
        osc.onended = () => this.countInOscs.delete(osc);
      }
      const endAt = start + beats * spb;
      this.countInTimer = setTimeout(() => { this.countInResolve = null; resolve(); }, Math.max(0, (endAt - this.ctx.currentTime) * 1000));
    });
  }
  /** Stop any in-progress count-in (silences its clicks and resolves the promise). */
  cancelCountIn() {
    const now = this.ctx.currentTime;
    for (const o of this.countInOscs) { try { o.stop(now); } catch {} }
    this.countInOscs.clear();
    clearTimeout(this.countInTimer); this.countInTimer = null;
    if (this.countInResolve) { const r = this.countInResolve; this.countInResolve = null; r(); }
  }

  // ---- metering --------------------------------------------------------
  private peakOf(an: AnalyserNode): number {
    an.getFloatTimeDomainData(this.meterBuf);
    let peak = 0;
    for (let i = 0; i < this.meterBuf.length; i++) {
      const v = Math.abs(this.meterBuf[i]);
      if (v > peak) peak = v;
    }
    return peak;
  }
  levels(): { master: number; tracks: Record<string, number> } {
    const tracks: Record<string, number> = {};
    for (const [id, ch] of this.channels) tracks[id] = this.peakOf(ch.analyser);
    return { master: this.peakOf(this.masterAnalyser), tracks };
  }
}
